// Mirrored from mju-ai-workbench/shared/schemas/nodeDelegates.ts (eb6c7543, D177) — keep the two identical but for this line and the import extensions.
/**
 * The people a node may run work for ("Nodes that run your work", D177;
 * docs/plans/2026-10-secrets-scopes.md §3 and §5 item 4): one wire shape for
 * both homes — the organisation's (AIOE 0.6.1, `packages/api/src/routes/secrets.ts`,
 * whose answers this mirrors field for field) and a hub's (Teams edition,
 * desktop/src/main/teamHub/delegation.ts) — so Settings → Secrets uses one
 * client for either.
 *
 *   GET    /secrets/v1/nodes[?nodeId=]                    { me, delegates, nodes, admin?, people? }
 *   PUT    /secrets/v1/nodes/:nodeId/people/:principal    allow: { delegates } (that node's)
 *   DELETE /secrets/v1/nodes/:nodeId/people/:principal    withdraw: { delegates }
 *   PUT    /secrets/v1/nodes/self/held                    a node: { people: [{ principal, names, until? }] } → 204
 *
 * What a node holds for each person it runs work for (D177 follow-up): the
 * node reports the *names* of the secrets released to it per person — never
 * a value — whenever that changes and every fifteen minutes, and the answer
 * to `GET` carries them as `held` (the caller's own rows; everyone's for an
 * administrator), so Settings → Secrets can say "holds GITHUB_TOKEN and
 * NPM_TOKEN for you now". Optional on both sides: a home that does not keep
 * them answers 404 to the report and leaves `held` out.
 *
 * A person decides for themselves (`how: "consent"`); the organisation's
 * administrators — the hub's owner on a hub — may assign anyone
 * (`"assigned"`). On a hub a node also acts for the person it joined as
 * (`"joined"`: listed, never removable short of removing the node).
 * A node that names someone it may not act for is answered
 * `403 { error: "not_delegated", message, principal }`.
 *
 * What a hub adds to the organisation's answer (optional, so the
 * organisation's parses unchanged): `admin` (the caller may assign anyone),
 * `people` (the team, for the owner's picker) and each node's `name` and
 * `personId` (whose node it is). AIOE should add `admin` (and a node's
 * display name) to match.
 *
 * Pure: no node imports.
 */
import { z } from "zod";
import { secretNameSchema } from "./secrets.js";
export const NODE_DELEGATE_HOW = ["consent", "assigned", "joined"];
export const nodeDelegateHowSchema = z.enum(NODE_DELEGATE_HOW);
/** One person a node may run work for. */
export const nodeDelegateSchema = z.object({
    nodeId: z.string().min(1).max(200),
    principal: z.string().min(1).max(200),
    /** `consent`: the person said so; `assigned`: an administrator (the hub's owner) did; `joined`: the node is theirs. */
    how: nodeDelegateHowSchema,
    /** Who made it so. */
    by: z.string().max(200),
    at: z.string(),
    /** The person's name, where the home knows it (a hub always does). */
    name: z.string().max(300).optional(),
});
/** A node a person may let run their work. */
export const delegableNodeSchema = z.object({
    deviceId: z.string().min(1).max(200),
    hostname: z.string().max(300).optional(),
    online: z.boolean().optional(),
    /** A hub: what the node is called on the team. */
    name: z.string().max(300).optional(),
    /** A hub: the person the node joined as. */
    personId: z.string().max(200).optional(),
});
/** One person's set on one node, as the node last reported it. Names only, never a value. */
export const nodeHeldSchema = z.object({
    nodeId: z.string().min(1).max(200),
    principal: z.string().min(1).max(200),
    /** The person's name, where the home knows it. */
    name: z.string().max(300).optional(),
    /** The secrets' names, sorted. */
    names: z.array(secretNameSchema).max(200),
    /** When the node reported it. */
    at: z.string(),
    /** When the release it holds ends, at the latest. */
    until: z.string().max(40).optional(),
});
/** `PUT /secrets/v1/nodes/self/held`: the node's whole report (a person left out holds nothing now). */
export const nodeHeldReportSchema = z.object({
    people: z.array(z.object({ principal: z.string().min(1).max(200), names: z.array(secretNameSchema).max(200), until: z.string().max(40).optional() })).max(200),
});
/** `GET /secrets/v1/nodes`. A person sees their own rows; an administrator (the hub's owner) everyone's. */
export const nodeDelegatesAnswerSchema = z.object({
    me: z.string().min(1).max(200),
    delegates: z.array(nodeDelegateSchema),
    nodes: z.array(delegableNodeSchema),
    /** The caller may assign anyone (an administrator, the hub's owner). Absent: only themselves. */
    admin: z.boolean().optional(),
    /** A hub, for its owner: the team, for the picker. */
    people: z.array(z.object({ id: z.string().min(1).max(200), name: z.string().max(300), role: z.string().max(40).optional() })).optional(),
    /** What each node holds for each person now, by name (`PUT …/self/held`): the caller's own rows, everyone's for an administrator. */
    held: z.array(nodeHeldSchema).optional(),
});
/** `PUT` / `DELETE /secrets/v1/nodes/:nodeId/people/:principal`. */
export const nodeDelegatesChangeAnswerSchema = z.object({ delegates: z.array(nodeDelegateSchema) });
/** Where the routes live under a home's API base. */
export const NODE_DELEGATES_PATH = "/secrets/v1/nodes";
// ── A node that does not run the person's work (said where they started it) ──
/** The word both sides use when a node may not run a person's work. */
export const NOT_DELEGATED = "not_delegated";
/** A node's name for a sentence: its name, else "A node (…last six)" — never a bare id. */
export function nodeWords(node) {
    return node.nodeName?.trim() ? node.nodeName.trim().slice(0, 80) : `A node (…${node.nodeId.slice(-6)})`;
}
/**
 * The one sentence the person who started the work reads: "build-01 does not run your work yet: you have not allowed
 * it, and the hub's owner has not assigned it to you." `home`: who else decides.
 */
export function notDelegatedSentence(node, home = "organisation") {
    return `${nodeWords(node)} does not run your work yet: you have not allowed it, and ${home === "hub" ? "the hub's owner has not" : "no administrator has"} assigned it to you.`;
}
/** The action that fixes it, at most 60 characters (an Inbox action's label): "Allow build-01 to run your work". */
export function allowNodeLabel(node) {
    const words = nodeWords(node);
    const room = 60 - "Allow  to run your work".length;
    return `Allow ${words.length > room ? `${words.slice(0, room - 1)}…` : words} to run your work`;
}
/** The Inbox key for a node's refusal: one record per node, filed again (not twice) when it refuses again. */
export const notDelegatedKey = (nodeId) => `node-not-delegated:${nodeId}`;
/** The action's `rpc`: run by the desktop itself (it lets the node run the signed-in person's own work), never a host. */
export const ALLOW_NODE_RPC = "secretVault.allowNode";
/** Where a node reports what it holds for each person. */
export const NODE_HELD_PATH = "/secrets/v1/nodes/self/held";
/** `/secrets/v1/nodes/:nodeId/people/:principal`. */
export function nodeDelegatePath(nodeId, principal) {
    return `${NODE_DELEGATES_PATH}/${encodeURIComponent(nodeId)}/people/${encodeURIComponent(principal)}`;
}
//# sourceMappingURL=nodeDelegates.js.map